import { useEffect, useState } from "react";
import instance from "api/cmsapi";
import axios from "api/axios";
import {
  extractIndicatorListFromCmsPayload,
  findCmsIndicatorForSlug,
  resolveDpatCmsYear,
} from "utils/dpatIndicatorCmsHelpers";

/**
 * CMS indicator row for `slug` + optional DHIS2 program description as MoV fallback when CMS has none.
 *
 * @param {string|number|null|undefined} year
 * @param {string} slug Normalized slug without year, e.g. `c1.0-1.1`
 * @param {string|null|undefined} explicitDhis2ProgramId Optional `/programs/{id}` for MoV description
 */
export function useDpatIndicatorCmsAndMov(year, slug, explicitDhis2ProgramId) {
  const [cmsIndicator, setCmsIndicator] = useState(null);
  const [meansOfVerificationFromAxios, setMeansOfVerificationFromAxios] = useState(null);
  const [cmsLoading, setCmsLoading] = useState(false);

  const programId = explicitDhis2ProgramId || null;

  const cmsYear = resolveDpatCmsYear(year);

  useEffect(() => {
    if (cmsYear == null || cmsYear === "" || !slug) {
      setCmsIndicator(null);
      setMeansOfVerificationFromAxios(null);
      setCmsLoading(false);
      return undefined;
    }
    let cancelled = false;

    const run = async () => {
      setCmsLoading(true);
      setCmsIndicator(null);
      setMeansOfVerificationFromAxios(null);

      try {
        const cmsPromise = instance.get(`dpat-indicator/year/${cmsYear}`).catch(() => null);
        const progPromise = programId
          ? axios
              .get(`/programs/${programId}`, { params: { fields: "id,displayName,description" } })
              .catch(() => null)
          : Promise.resolve(null);

        const [cmsRes, progRes] = await Promise.all([cmsPromise, progPromise]);
        if (cancelled) return;

        if (cmsRes?.data) {
          const list = extractIndicatorListFromCmsPayload(cmsRes.data);
          setCmsIndicator(findCmsIndicatorForSlug(list, slug));
        } else {
          setCmsIndicator(null);
        }

        const desc = progRes?.data?.description;
        if (typeof desc === "string" && desc.trim()) {
          setMeansOfVerificationFromAxios(desc.trim());
        }
      } catch {
        if (!cancelled) {
          setCmsIndicator(null);
          setMeansOfVerificationFromAxios(null);
        }
      } finally {
        if (!cancelled) setCmsLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [cmsYear, slug, programId]);

  return { cmsIndicator, meansOfVerificationFromAxios, cmsLoading };
}
